/**
 * Action log — event-based user-action telemetry (#1895).
 *
 * Lightweight ring-buffered record of discrete actions the rep took
 * (clicks, form submits, navigation, API calls, errors). NOT a full
 * DOM session replay — we only capture WHAT happened and in what order,
 * never field values or visual state. Goal: when a rep submits a
 * Send Device Report, we can see the exact path they took to the bug
 * without needing to reconstruct it from CallLog forensics.
 *
 * Buffer cap: last 100 events (~5–10 KB worth). Bundled into every
 * diagnostics submission via collectDiagnostics().
 */

export type ActionEventKind = 'click' | 'submit' | 'nav' | 'api' | 'error' | 'visibility';

export interface ActionEvent {
  /** Unix ms. */
  at: number;
  kind: ActionEventKind;
  /** Human-readable label — button text, form id, page title, etc. (≤60 chars). */
  target?: string;
  /** Coarse CSS-ish selector for tracing the DOM node (≤80 chars). */
  selector?: string;
  /** For nav events: previous path. */
  from?: string;
  /** For nav events: new path. */
  to?: string;
  /** For api events: HTTP method. */
  method?: string;
  /** For api events: URL (path only, no query). */
  url?: string;
  /** For api events: HTTP status code. */
  status?: number;
  /** For api events: elapsed milliseconds. */
  ms?: number;
  /** Generic free-form note (e.g. form field names, error message). */
  note?: string;
}

const BUFFER_CAP = 100;
const buffer: ActionEvent[] = [];

export function logEvent(ev: Omit<ActionEvent, 'at'> & { at?: number }): void {
  buffer.push({ ...ev, at: ev.at ?? Date.now() });
  while (buffer.length > BUFFER_CAP) buffer.shift();
}

/** Snapshot of recent events (chronological, oldest first). */
export function getActionLog(limit?: number): ActionEvent[] {
  if (limit && limit < buffer.length) return buffer.slice(-limit);
  return buffer.slice();
}

export function clearActionLog(): void {
  buffer.length = 0;
}

/**
 * Internal: describe a DOM node as a short label + selector. Walks
 * upward to the nearest meaningful ancestor (button / a / [role=button]
 * / clickable card) so a click on a <span> inside a button records the
 * button instead of the span.
 */
export function describeNode(el: Element | null): { target: string; selector: string } {
  if (!el) return { target: '<root>', selector: 'root' };
  let cur: Element | null = el;
  let depth = 0;
  while (cur && cur !== document.body && depth < 6) {
    const tag = cur.tagName.toLowerCase();
    const role = cur.getAttribute('role');
    const isClickable =
      tag === 'button'
      || tag === 'a'
      || role === 'button'
      || role === 'link'
      || cur.hasAttribute('onclick');
    if (isClickable) break;
    cur = cur.parentElement;
    depth += 1;
  }
  const node = cur ?? el;
  const tag = node.tagName.toLowerCase();
  const id = node.id ? `#${node.id}` : '';
  const cls =
    typeof node.className === 'string' && node.className
      ? '.' + node.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.')
      : '';
  const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
  return {
    target: text || `<${tag}>`,
    selector: `${tag}${id}${cls}`.slice(0, 80),
  };
}
